import { Box } from '@mui/material';
import { FC, FocusEventHandler, SyntheticEvent, useMemo } from 'react';
import { Language } from 'iso-639-2';
import { Autocomplete, AutocompleteProps } from '@vxui/components';
import { languageItems } from '../../../models';
import { FieldConfig, useField } from 'formik';

const getLangLabel = (lang: Language) => lang.name;
const promotedLangs = ['eng', 'fra', 'ger', 'gre', 'ita', 'pol', 'por', 'spa', 'und', 'org'];

type LanguageFieldProps = {
  value: string;
  onChange: (event: SyntheticEvent, value: string) => void;
} & Partial<Omit<AutocompleteProps<Language, false, true, false>, 'value' | 'onChange'>>;

export const LanguageField: FC<LanguageFieldProps> = ({ value, onChange, ...otherProps }) => {
  const options = useMemo(
    () =>
      Object.values(languageItems).sort((a, b) => {
        const nameA = getLangLabel(a);
        const nameB = getLangLabel(b);
        const scoreA = Number(promotedLangs.includes(a.iso6392B));
        const scoreB = Number(promotedLangs.includes(b.iso6392B));
        if (scoreA !== scoreB) return scoreB - scoreA;
        return nameA.localeCompare(nameB);
      }),
    [],
  );
  const langValue = languageItems[value ?? ''] || null;

  return (
    <Autocomplete
      {...otherProps}
      value={langValue}
      options={options}
      componentsProps={{
        paper: {
          sx: {
            width: 260,
          },
        },
      }}
      fullWidth
      autoHighlight
      getOptionLabel={getLangLabel}
      clearIcon={false}
      renderOption={(props, option) => (
        <Box {...props} component="li">
          {getLangLabel(option)}
        </Box>
      )}
      onChange={(event, value) => onChange && onChange(event, value?.iso6392B ?? '')}
    />
  );
};

const getErrorProps = (touched: boolean, error?: string) =>
  touched && error ? { error: !!error, helperText: error } : {};

export const LanguageFieldFormik = (props: FieldConfig & Omit<LanguageFieldProps, 'value' | 'onChange'>) => {
  const { name } = props;
  const [input, { error, touched }, { setValue, setTouched }] = useField(name);

  const handleOnBlur: FocusEventHandler<HTMLDivElement> = () => setTouched(true);

  return (
    <LanguageField
      {...props}
      {...input}
      multiple={false}
      onBlur={handleOnBlur}
      onChange={(_, value) => {
        setValue(value, true);
      }}
      {...getErrorProps(touched, error)}
    />
  );
};
